import React, { useCallback, useEffect, useMemo, useState, type JSX } from 'react';

import { Map as ImMap } from 'immutable';
import { DateTime } from 'luxon';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { makeStyles } from 'tss-react/mui';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import { OverflowTooltip } from 'components/OverflowTooltip';
import QAmountField from 'components/QAmountField';
import SPayeeField from 'components/SPayeeField';
import CategoryField from 'components/QuickenControls/CategoryField';

import { accountsSelectors, type Account } from 'app-components/flux/accounts';
import type { ReviewLlmOriginalTxnValues } from 'app-components/flux/ai-chat/tools/handlers/transactions';
import { getCategoriesById } from 'app-components/flux/categories/categoriesSelectors';
import type { Category } from 'app-components/flux/categories/categoriesTypes';
import { CoaTypeEnum, type ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { getBalanceAdjustmentString } from 'app-components/flux/chart-of-accounts/chartOfAccountsUtils';
import { rootUiActions } from 'app-components/flux/root-ui';
import { transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { getLogger } from 'app-components/utils/core';

import { useAppDispatch, useAppSelector } from 'hooks/redux';

import useTxnActions from 'data/transactionActions/useTxnActions';

export const DIALOG_TYPE_REVIEW_CATEGORY = 'LLM_CATEGORIZE_TRANSACTIONS';
export type { ReviewLlmOriginalTxnValues };
const log = getLogger('components/Dialogs/ReviewLlmCategoriesDialog');

const useStyles = makeStyles()((theme) => ({
  header: {
    marginBottom: theme.spacing(0.5),
  },
  selectAllRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(2),
  },
  selectAllLabel: {
    marginLeft: 0,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  transactionItem: {
    margin: theme.spacing(0.25),
    padding: theme.spacing(1, 2),
    paddingLeft: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  invalidTransaction: {
    backgroundColor: theme.palette.error.light,
    border: `1px solid ${theme.palette.error.main}`,
  },
  // Shared tracks across every summary row so variable date width cannot shift payee/category.
  // [checkbox] [date] [payee/category] [amount/account] [edit]
  transactionHeader: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(5.5rem, 7rem) minmax(0, 1fr) auto auto',
    columnGap: theme.spacing(1),
    alignItems: 'center',
  },
  date: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    alignSelf: 'center',
  },
  stackedDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  stackedRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
    justifySelf: 'end',
    minWidth: 0,
  },
  amountFieldInput: {
    textAlign: 'right',
  },
  accountName: {
    textAlign: 'right',
    color: theme.palette.text.secondary,
    minWidth: 0,
    maxWidth: '6.5rem',
  },
  helperText: {
    marginTop: theme.spacing(0.5),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  changeText: {
    display: 'flex',
    alignItems: 'baseline',
    minWidth: 0,
    maxWidth: '100%',
  },
  truncatedLabel: {
    minWidth: 0,
    maxWidth: 'calc((100% - 2rem) / 2)',
  },
  existingValue: {
    color: theme.palette.text.tertiary,
    textDecoration: 'line-through',
  },
  changeArrow: {
    color: theme.palette.text.tertiary,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    margin: theme.spacing(0, 0.5),
  },
  editPanel: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  editField: {
    flex: 1,
    minWidth: 0,
  },
  rowCheckbox: {
    padding: theme.spacing(0.5),
    alignSelf: 'center',
  },
}));

type ReviewResult = {
  success: boolean;
  updatedCount?: number;
  errors?: string[];
};

const getCategoryDisplayName = (
  coa: ChartOfAccountProps | null | undefined,
  categoriesById: ImMap<string, Category>,
  accountsById: ImMap<string, Account>,
): string => {
  if (!coa?.id) {
    return 'Uncategorized';
  }
  switch (coa.type) {
    case CoaTypeEnum.UNCATEGORIZED:
    case CoaTypeEnum.NONE:
      return 'Uncategorized';
    case CoaTypeEnum.ACCOUNT: {
      const resolvedAccount = accountsById.get(coa.id);
      return resolvedAccount?.name ?? (resolvedAccount as Account | null | undefined)?.get?.('name') ?? 'Uncategorized';
    }
    case CoaTypeEnum.CATEGORY: {
      const resolvedCategory = categoriesById.get(coa.id);
      return (
        resolvedCategory?.name ?? (resolvedCategory as Category | null | undefined)?.get?.('name') ?? 'Uncategorized'
      );
    }
    case CoaTypeEnum.BALANCE_ADJUSTMENT:
      return getBalanceAdjustmentString(coa.id);
    // Synthetic tree-grouping node types; isCoaSettable excludes them from ever being set on a real transaction.
    case CoaTypeEnum.CATEGORY_USAGE_TYPE:
    case CoaTypeEnum.DISPLAY_NODE:
      return 'NA';
    default: {
      const exhaustiveCheck: never = coa.type;
      return exhaustiveCheck;
    }
  }
};

const coaEquals = (a: ChartOfAccountProps | null | undefined, b: ChartOfAccountProps | null | undefined): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.type === b.type && a.id === b.id;
};

const payeeEquals = (a: string | null | undefined, b: string | null | undefined): boolean => (a ?? '') === (b ?? '');

const getTxnSortMillis = (txn: TransactionRecord): number => {
  const txnDate = transactionsUtils.dateTimeFromTxn(txn);
  return txnDate.isValid ? txnDate.toMillis() : Number.POSITIVE_INFINITY;
};

const compareTransactionsChronologically = (a: TransactionRecord, b: TransactionRecord): number => {
  const millisDiff = getTxnSortMillis(a) - getTxnSortMillis(b);
  if (millisDiff !== 0) {
    return millisDiff;
  }
  return (a.id ?? '').localeCompare(b.id ?? '');
};

type ChangeLabelProps = {
  id?: string;
  existingLabel: string;
  suggestedLabel: string;
  suggestedVariant: 'paragraphMedium' | 'paragraphRegular';
  suggestedColor?: 'text.primary' | 'text.secondary';
  classes: ReturnType<typeof useStyles>['classes'];
};

const ChangeLabel = ({
  id,
  existingLabel,
  suggestedLabel,
  suggestedVariant,
  suggestedColor,
  classes,
}: ChangeLabelProps): JSX.Element => (
  <Typography id={id} variant='paragraphRegular' component='span' className={classes.changeText}>
    <OverflowTooltip title={existingLabel}>
      <Typography
        variant='paragraphRegular'
        component='span'
        noWrap
        tabIndex={0}
        className={`${classes.existingValue} ${classes.truncatedLabel}`}
      >
        {existingLabel}
      </Typography>
    </OverflowTooltip>
    <Typography variant='paragraphRegular' component='span' className={classes.changeArrow}>
      {' → '}
    </Typography>
    <OverflowTooltip title={suggestedLabel}>
      <Typography
        variant={suggestedVariant}
        component='span'
        color={suggestedColor}
        noWrap
        tabIndex={0}
        className={classes.truncatedLabel}
      >
        {suggestedLabel}
      </Typography>
    </OverflowTooltip>
  </Typography>
);

type TransactionItemProps = {
  transaction: TransactionRecord;
  originalValues?: ReviewLlmOriginalTxnValues;
  isInvalidCategory: boolean;
  isSelected: boolean;
  onCategoryChange: (txnId: string, newCoa: ChartOfAccountProps) => void;
  onPayeeChange: (txnId: string, newPayee: string) => void;
  onSelectionChange: (txnId: string, selected: boolean) => void;
  categoriesById: ImMap<string, Category>;
  accountsById: ImMap<string, Account>;
  classes: ReturnType<typeof useStyles>['classes'];
  cx: ReturnType<typeof useStyles>['cx'];
};

const TransactionItem = ({
  transaction,
  originalValues,
  isInvalidCategory,
  isSelected,
  onCategoryChange,
  onPayeeChange,
  onSelectionChange,
  categoriesById,
  accountsById,
  classes,
  cx,
}: TransactionItemProps): JSX.Element | null => {
  const txnId = transaction.id;

  const [isEditing, setIsEditing] = useState(false);

  if (!txnId) {
    log.warn('Encountered a transaction without an id; skipping render.');
    return null;
  }

  const editPanelId = `review-llm-edit-panel-${txnId}`;
  const suggestedCategoryName = getCategoryDisplayName(transaction.coa, categoriesById, accountsById);
  const existingCategoryName = originalValues
    ? getCategoryDisplayName(originalValues.coa, categoriesById, accountsById)
    : null;
  const showCategoryChange = Boolean(originalValues) && !coaEquals(originalValues?.coa, transaction.coa);
  const suggestedPayee = transaction.payee || 'No payee';
  const existingPayee = originalValues?.payee || 'No payee';
  const showPayeeChange = Boolean(originalValues) && !payeeEquals(originalValues?.payee, transaction.payee);
  const accountName = accountsById.get(transaction.accountId || '')?.name || 'Unknown';
  const txnDate = transactionsUtils.dateTimeFromTxn(transaction);
  const dayString = txnDate.isValid ? txnDate.toLocaleString(DateTime.DATE_MED) : '—';

  return (
    <Paper
      id={`review-llm-item-${txnId}`}
      className={cx(classes.transactionItem, isInvalidCategory && classes.invalidTransaction)}
      elevation={1}
    >
      <Box className={classes.transactionHeader}>
        <Checkbox
          id={`review-llm-checkbox-${txnId}`}
          size='small'
          className={classes.rowCheckbox}
          checked={isSelected}
          onChange={(event) => onSelectionChange(txnId, event.target.checked)}
          inputProps={{
            'aria-label': `Include transaction ${suggestedPayee} on ${dayString} in update`,
          }}
        />
        <Typography
          id={`review-llm-date-${txnId}`}
          variant='paragraphRegular'
          color='text.secondary'
          className={classes.date}
        >
          {dayString}
        </Typography>
        <Box className={classes.stackedDetails}>
          {showPayeeChange && existingPayee ? (
            <ChangeLabel
              id={`review-llm-payee-label-${txnId}`}
              existingLabel={existingPayee}
              suggestedLabel={suggestedPayee}
              suggestedVariant='paragraphMedium'
              classes={classes}
            />
          ) : (
            <Typography id={`review-llm-payee-label-${txnId}`} variant='paragraphMedium'>
              {suggestedPayee}
            </Typography>
          )}
          <Box>
            {showCategoryChange && existingCategoryName ? (
              <ChangeLabel
                id={`review-llm-category-label-${txnId}`}
                existingLabel={existingCategoryName}
                suggestedLabel={suggestedCategoryName}
                suggestedVariant='paragraphMedium'
                suggestedColor='text.secondary'
                classes={classes}
              />
            ) : (
              <Typography id={`review-llm-category-label-${txnId}`} variant='paragraphRegular' color='text.secondary'>
                {suggestedCategoryName}
              </Typography>
            )}
            {isInvalidCategory && (
              <Typography variant='paragraphRegular' color='error' className={classes.helperText}>
                Please select a valid category
              </Typography>
            )}
          </Box>
        </Box>
        <Box className={classes.stackedRight}>
          <QAmountField
            id={`review-llm-amount-${txnId}`}
            value={Number(transaction.amount ?? 0)}
            currency={transaction.currency ?? 'USD'}
            editable={false}
            plain
            variant='paragraphMedium'
            showSign='positive'
            showColor='positive'
            classes={{ input: classes.amountFieldInput }}
          />
          <OverflowTooltip title={accountName}>
            <Typography
              id={`review-llm-account-${txnId}`}
              variant='captionRegular'
              color='text.secondary'
              noWrap
              tabIndex={0}
              className={classes.accountName}
            >
              {accountName}
            </Typography>
          </OverflowTooltip>
        </Box>
        <IconButton
          id={`review-llm-edit-button-${txnId}`}
          size='small'
          variant='tonal'
          shape='rounded'
          color={isEditing ? 'primary' : 'default'}
          aria-label={isEditing ? 'Close edit' : 'Edit transaction'}
          aria-expanded={isEditing}
          aria-controls={isEditing ? editPanelId : undefined}
          onClick={() => setIsEditing((prev) => !prev)}
        >
          {isEditing ? <KeyboardArrowUpIcon fontSize='small' /> : <EditRoundedIcon fontSize='small' />}
        </IconButton>
      </Box>

      {isEditing && (
        <Box id={editPanelId} className={classes.editPanel}>
          <Box className={classes.editField}>
            <SPayeeField
              id={`review-llm-payee-${txnId}`}
              defaultValue={transaction.payee ?? ''}
              onChange={(newPayee: string) => {
                onPayeeChange(txnId, newPayee);
              }}
              fullWidth
              textFieldProps={{
                variant: 'filled',
                label: 'Payee',
                margin: 'dense',
              }}
            />
          </Box>
          <Box className={classes.editField}>
            <CategoryField
              id={`review-llm-category-${txnId}`}
              editable
              textFieldVariant='filled'
              label='Category'
              value={transaction.coa ?? undefined}
              onChange={(newCoa?: ChartOfAccountProps | null) => {
                if (newCoa) {
                  onCategoryChange(txnId, newCoa);
                }
              }}
              margin='dense'
              helperText={isInvalidCategory ? 'Please select a valid category' : undefined}
              error={isInvalidCategory}
              width='100%'
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

type ReviewLlmCategoriesDialogProps = {
  transactions?: TransactionRecord[];
  /** Pre-suggestion coa/payee keyed by transaction id (for existing → suggested display). */
  originalByTxnId?: Record<string, ReviewLlmOriginalTxnValues>;
  callback?: (result: ReviewResult) => void;
  dialogId?: string;
};

const EMPTY_TRANSACTIONS: TransactionRecord[] = [];
const EMPTY_ORIGINALS: Record<string, ReviewLlmOriginalTxnValues> = {};

const ReviewLlmCategoriesDialog = ({
  transactions = EMPTY_TRANSACTIONS,
  originalByTxnId = EMPTY_ORIGINALS,
  callback,
  dialogId,
}: ReviewLlmCategoriesDialogProps): JSX.Element => {
  const { classes, cx } = useStyles();
  const dispatch = useAppDispatch();
  const categoriesById = useAppSelector(getCategoriesById);
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const { saveTransactions } = useTxnActions();

  const sanitizedTransactions = useMemo(
    () => transactions.filter((txn): txn is TransactionRecord & { id: string } => Boolean(txn?.id)),
    [transactions],
  );

  const initialMap = useMemo(
    () =>
      ImMap<string, TransactionRecord>(
        sanitizedTransactions.map((txn) => [txn.id as string, txn] as [string, TransactionRecord]),
      ),
    [sanitizedTransactions],
  );

  const [editedTransactions, setEditedTransactions] = useState(initialMap);
  const [selectedTxnIds, setSelectedTxnIds] = useState<ReadonlySet<string>>(
    () => new Set(initialMap.keySeq().toArray()),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setEditedTransactions(initialMap);
    setSelectedTxnIds(new Set(initialMap.keySeq().toArray()));
  }, [initialMap]);

  const closeDialog = useCallback(() => {
    dispatch(rootUiActions.removeDialog(dialogId));
  }, [dispatch, dialogId]);

  const isCategoryValid = useCallback(
    (coa: ChartOfAccountProps | null | undefined) => {
      if (!coa) return false;
      if (coa.type === 'UNCATEGORIZED' || coa.type === 'NONE') return false;
      if (coa.type === 'CATEGORY') {
        return categoriesById.has(coa.id);
      }
      return true;
    },
    [categoriesById],
  );

  const handleCategoryChange = useCallback((txnId: string, newCoa: ChartOfAccountProps) => {
    setEditedTransactions((prev) => {
      const txn = prev.get(txnId);
      if (!txn) {
        log.warn('Transaction not found while updating category:', txnId);
        return prev;
      }
      return prev.set(txnId, txn.set('coa', newCoa));
    });
  }, []);
  const handlePayeeChange = useCallback((txnId: string, newPayee: string) => {
    setEditedTransactions((prev) => {
      const txn = prev.get(txnId);
      if (!txn) {
        log.warn('Transaction not found while updating payee:', txnId);
        return prev;
      }
      return prev.set(txnId, txn.set('payee', newPayee));
    });
  }, []);
  const handleSelectionChange = useCallback((txnId: string, selected: boolean) => {
    setSelectedTxnIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(txnId);
      } else {
        next.delete(txnId);
      }
      return next;
    });
  }, []);
  const handleSelectAllChange = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedTxnIds(new Set(editedTransactions.keySeq().toArray()));
      } else {
        setSelectedTxnIds(new Set());
      }
    },
    [editedTransactions],
  );

  const transactionsToRender = useMemo(
    () => editedTransactions.valueSeq().toArray().sort(compareTransactionsChronologically),
    [editedTransactions],
  );
  const selectedTransactions = useMemo(
    () =>
      transactionsToRender.filter((txn): txn is TransactionRecord & { id: string } =>
        Boolean(txn.id && selectedTxnIds.has(txn.id)),
      ),
    [transactionsToRender, selectedTxnIds],
  );
  const selectedCount = selectedTransactions.length;
  const allSelected = transactionsToRender.length > 0 && selectedCount === transactionsToRender.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const hasUncategorizedSelected = useMemo(
    () => selectedTransactions.some((txn) => !isCategoryValid(txn.coa)),
    [selectedTransactions, isCategoryValid],
  );

  const sendCallback = useCallback(
    (result: ReviewResult) => {
      callback?.(result);
    },
    [callback],
  );

  const handleCancel = useCallback(() => {
    sendCallback({
      success: false,
      errors: ['User cancelled'],
    });
    closeDialog();
  }, [sendCallback, closeDialog]);

  const handleConfirm = useCallback(() => {
    const transactionsToSave = selectedTransactions;
    if (!transactionsToSave.length) {
      sendCallback({
        success: false,
        errors: ['No transactions available to update'],
      });
      return;
    }

    setIsSubmitting(true);

    const onResolve = (result?: unknown) => {
      const updatedCount = Array.isArray(result) ? result.length : transactionsToSave.length;
      sendCallback({
        success: true,
        updatedCount,
      });
      setIsSubmitting(false);
      closeDialog();
    };

    const onReject = (error?: unknown) => {
      log.error('Save transactions failed', error);
      sendCallback({
        success: false,
        errors: ['Failed to save transactions'],
      });
      setIsSubmitting(false);
      closeDialog();
    };

    saveTransactions(
      transactionsToSave,
      {
        callback: (optimisticResult: unknown) => {
          if (!optimisticResult) {
            setIsSubmitting(false);
          }
        },
      },
      onResolve,
      onReject,
    );
  }, [closeDialog, saveTransactions, selectedTransactions, sendCallback]);

  const disableConfirm = isSubmitting || hasUncategorizedSelected || selectedCount === 0;
  const appliedCountLabel =
    selectedCount === 1 ? '1 transaction will be updated' : `${selectedCount} transactions will be updated`;

  return (
    <StdDialog
      onClose={handleCancel}
      open
      sharedcomponentid='review-llm-categories-dialog'
      showCloseButton
      size='md'
      title='Review transaction details'
      paperProps={{
        sx: { overflowY: 'hidden' },
      }}
    >
      <StdDialogContent>
        <Box className={classes.header}>
          <Typography variant='paragraphRegular' color='text.secondary'>
            Quicken AI reviewed {transactionsToRender.length}{' '}
            {transactionsToRender.length === 1 ? 'transaction' : 'transactions'} and suggests updating their details.
            Uncheck any you&apos;d rather leave as is, or edit a suggestion directly.
          </Typography>
          {transactionsToRender.length > 0 && (
            <Box className={classes.selectAllRow}>
              <FormControlLabel
                className={classes.selectAllLabel}
                control={
                  <Checkbox
                    size='small'
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(event) => handleSelectAllChange(event.target.checked)}
                  />
                }
                label={<Typography variant='paragraphMedium'>Select all</Typography>}
              />
              <Typography variant='paragraphRegular' color='text.tertiary'>
                {selectedCount} selected
              </Typography>
            </Box>
          )}
          {hasUncategorizedSelected && (
            <Typography variant='paragraphRegular' color='error' className={classes.helperText}>
              Assign a valid category to each selected transaction before confirming.
            </Typography>
          )}
        </Box>

        {transactionsToRender.length ? (
          <Box className={classes.list}>
            {transactionsToRender.map((txn) => {
              const txnId = txn.id as string;
              const isSelected = selectedTxnIds.has(txnId);
              return (
                <TransactionItem
                  key={txnId}
                  transaction={txn}
                  originalValues={originalByTxnId[txnId]}
                  isInvalidCategory={isSelected && !isCategoryValid(txn.coa)}
                  isSelected={isSelected}
                  onCategoryChange={handleCategoryChange}
                  onPayeeChange={handlePayeeChange}
                  onSelectionChange={handleSelectionChange}
                  categoriesById={categoriesById}
                  accountsById={accountsById}
                  classes={classes}
                  cx={cx}
                />
              );
            })}
          </Box>
        ) : (
          <Box className={classes.emptyState}>
            <Typography variant='paragraphRegular'>No transactions to review.</Typography>
          </Box>
        )}
      </StdDialogContent>

      <StdDialogActions
        divider
        tertiaryNode={
          <Typography variant='captionRegular' color='text.secondary'>
            {appliedCountLabel}
          </Typography>
        }
        primary={{
          id: 'review-llm-categories-confirm',
          children: 'Confirm',
          onClick: handleConfirm,
          disabled: disableConfirm,
          loading: isSubmitting,
        }}
        secondary={{
          id: 'review-llm-categories-cancel',
          children: 'Cancel',
          onClick: handleCancel,
          disabled: isSubmitting,
        }}
      />
    </StdDialog>
  );
};

export default ReviewLlmCategoriesDialog;
